"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFuncion } from "../context/FuncionContext";

/**
 * useFacultyScope
 * - Centraliza selección de facultad para:
 *   - admin global (admin/administrador/superadmin)
 *   - admin_facultad / coordinador_facultad (scoped)
 * - Respeta ruta /admin/facultades/:facultadId/cursos (forzado)
 * - Auto-selecciona si solo hay 1 facultad (para admin_facultad)
 * - Persiste selección en localStorage (cuando NO está forzado)
 */
export default function useFacultyScope() {
  const { user, currentRole } = useAuth();
  const { fetchFacultades } = useFuncion();

  const { facultadId: facultadIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const facultadIdQuery = searchParams.get("facultad_id");
  const forcedFacultyId = useMemo(() => {
    const n = Number(facultadIdParam);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [facultadIdParam]);

  // “compat”: si alguien usa query facultad_id
  const queryFacultyId = useMemo(() => {
    const n = Number(facultadIdQuery);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [facultadIdQuery]);

  const roleLow = useMemo(() => String(currentRole || "").toLowerCase().trim(), [currentRole]);

  const isGlobalAdmin = useMemo(
    () => roleLow === "admin" || roleLow === "administrador" || roleLow === "superadmin",
    [roleLow]
  );

  const isFacultyAdminActive = useMemo(
    () => roleLow === "admin_facultad" || roleLow === "coordinador_facultad",
    [roleLow]
  );

  // Opciones para admin_facultad/coordinador: vienen del token/user.admin_facultades
  const myOptions = useMemo(() => {
    const raw = Array.isArray(user?.admin_facultades) ? user.admin_facultades : [];

    // ✅ Incluye facultades donde tiene rol admin o coordinador
    // (si tú quieres solo admin, cambia rolesAllowed a ["admin"])
    const rolesAllowed = isFacultyAdminActive
      ? (roleLow === "admin_facultad" ? ["admin", "administrador"] : ["coordinador", "coordinador_facultad"])
      : ["admin", "administrador", "coordinador", "coordinador_facultad"];

    const list = raw
      .filter((f) => {
        const roles = Array.isArray(f?.roles) ? f.roles : [];
        if (!roles.length) return true; // si backend no manda roles, no bloquees
        return roles.some((r) => rolesAllowed.includes(String(r).toLowerCase().trim()));
      })
      .map((f) => {
        const id = Number(f?.facultad_id ?? f?.id);
        const nombre = f?.nombre || f?.facultad_nombre || `Facultad ${id}`;
        return { id, nombre };
      })
      .filter((x) => Number.isFinite(x.id) && x.id > 0);

    // únicos por id
    const byId = new Map();
    for (const x of list) if (!byId.has(x.id)) byId.set(x.id, x);
    return Array.from(byId.values());
  }, [user, isFacultyAdminActive, roleLow]);

  const myIds = useMemo(() => myOptions.map((x) => x.id), [myOptions]);

  const isFacultyScopedRoute = useMemo(() => Boolean(forcedFacultyId), [forcedFacultyId]);

  // Persistencia (solo cuando NO está forzado)
  const storageKey = "faculty_scope_selected_v1";
  const readStored = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  };

  const writeStored = (n) => {
    try {
      if (!n) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, String(n));
    } catch {}
  };

  // Facultades para selector:
  // - global admin => fetchFacultades
  // - faculty admin => myOptions
  const [faculties, setFaculties] = useState([]); // {id, nombre}[]
  const [loadingFaculties, setLoadingFaculties] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingFaculties(true);

        // Cargar desde BD para que las asignaciones nuevas no dependan de renovar el token.
        if (isGlobalAdmin || isFacultyAdminActive) {
          const res = await fetchFacultades?.({
            showAll: true,
            assignedOnly: isFacultyAdminActive,
          });
          const rows = Array.isArray(res) ? res : res?.facultades || res?.items || res?.data || [];
          if (!alive) return;

          const list = rows
            .map((f) => {
              const id = Number(f?.id ?? f?.facultad_id);
              const nombre = f?.nombre || f?.facultad_nombre || `Facultad ${id}`;
              return { id, nombre };
            })
            .filter((x) => Number.isFinite(x.id) && x.id > 0);

          setFaculties(list);
          return;
        }

        // Otros roles: vacío
        setFaculties([]);
      } catch {
        if (alive) setFaculties(isFacultyAdminActive ? myOptions : []);
      } finally {
        if (alive) setLoadingFaculties(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isGlobalAdmin, isFacultyAdminActive, fetchFacultades, myOptions]);

  const selectableFacultyIds = useMemo(() => {
    if (!isFacultyAdminActive) return myIds;
    const ids = faculties.map((faculty) => Number(faculty.id)).filter((id) => Number.isFinite(id) && id > 0);
    return ids.length > 0 ? ids : myIds;
  }, [isFacultyAdminActive, faculties, myIds]);

  // Selección
  const [selectedFacultyId, _setSelectedFacultyId] = useState(() => {
    // 1) Forzado por ruta
    if (forcedFacultyId) return forcedFacultyId;

    // 2) Compat por query
    if (queryFacultyId) return queryFacultyId;

    // 3) Persistido
    const stored = readStored();
    if (stored) return stored;

    // 4) Admin facultad: primera si existe
    if (isFacultyAdminActive && myIds.length > 0) return myIds[0];

    // 5) Global admin: null (que seleccione)
    return null;
  });

  // Si viene ruta forzada, siempre manda
  useEffect(() => {
    if (forcedFacultyId) _setSelectedFacultyId(forcedFacultyId);
  }, [forcedFacultyId]);

  // Auto-select para admin_facultad si tiene 1 sola (y no forzado)
  useEffect(() => {
    if (forcedFacultyId) return;

    if (isFacultyAdminActive) {
      if (selectableFacultyIds.length === 1) {
        _setSelectedFacultyId(selectableFacultyIds[0]);
        return;
      }
      // si tiene varias, validar que la actual esté dentro; si no, pone primera
      _setSelectedFacultyId((prev) => {
        const p = Number(prev);
        if (Number.isFinite(p) && selectableFacultyIds.includes(p)) return p;
        return selectableFacultyIds[0] ?? null;
      });
    }
  }, [forcedFacultyId, isFacultyAdminActive, selectableFacultyIds]);

  // Persistir cuando cambia selección (solo si NO forzado)
  useEffect(() => {
    if (forcedFacultyId) return;
    // solo persistimos para admins (global o faculty)
    if (!isGlobalAdmin && !isFacultyAdminActive) return;
    writeStored(selectedFacultyId);
  }, [forcedFacultyId, isGlobalAdmin, isFacultyAdminActive, selectedFacultyId]);

  const setSelectedFacultyId = useCallback(
    (next) => {
      const n = next == null ? null : Number(next);
      const val = Number.isFinite(n) && n > 0 ? n : null;

      // si está forzado, ignorar cambios
      if (forcedFacultyId) return;

      // faculty admin: solo puede elegir dentro de sus ids
      if (isFacultyAdminActive && val && !selectableFacultyIds.includes(val)) return;

      _setSelectedFacultyId(val);
    },
    [forcedFacultyId, isFacultyAdminActive, selectableFacultyIds]
  );

  // Facultad efectiva para queries backend
  const effectiveFacultyId = useMemo(() => {
    if (forcedFacultyId) return forcedFacultyId;

    const n = Number(selectedFacultyId);
    if (Number.isFinite(n) && n > 0) return n;

    // Faculty admin: fallback seguro a primera
    if (isFacultyAdminActive && selectableFacultyIds.length > 0) return selectableFacultyIds[0];

    return null;
  }, [forcedFacultyId, selectedFacultyId, isFacultyAdminActive, selectableFacultyIds]);

  // Mostrar selector?
  const shouldShowSelector = useMemo(() => {
    if (forcedFacultyId) return false;

    if (isGlobalAdmin) return true;

    if (isFacultyAdminActive) return selectableFacultyIds.length > 1;

    return false;
  }, [forcedFacultyId, isGlobalAdmin, isFacultyAdminActive, selectableFacultyIds.length]);

  const shouldAutoLockFaculty = useMemo(() => {
    if (forcedFacultyId) return true;
    if (isFacultyAdminActive && selectableFacultyIds.length === 1) return true;
    return false;
  }, [forcedFacultyId, isFacultyAdminActive, selectableFacultyIds.length]);

  return {
    // role flags
    isGlobalAdmin,
    isFacultyAdminActive,

    // scope
    forcedFacultyId,
    isFacultyScopedRoute,

    // options
    faculties,
    loadingFaculties,
    myOptions,
    myIds,

    // selection
    selectedFacultyId,
    setSelectedFacultyId,
    effectiveFacultyId,

    // UI
    shouldShowSelector,
    shouldAutoLockFaculty,

    // debug convenience
    pathname,
    roleLow,
  };
}
